import { cn } from '@ui/lib/utils'
import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cva, type VariantProps } from 'class-variance-authority'

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const tooltipContentVariants = cva(
  'z-tooltip animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 relative ' +
    'data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-2 ' +
    'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
  {
    variants: {
      size: {
        default: 'rounded-md text-sm px-3 py-1.5',
        small: 'rounded-sm p-xxs text-sm',
      },
      theme: {
        default: 'bg-white text-gray-950',
        dark: 'bg-gray-800 text-gray-50 [&_svg]:fill-gray-700',
      },
      defaultVariants: {
        size: 'small',
        theme: 'dark',
      },
    },
  },
)

export type ContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> &
  VariantProps<typeof tooltipContentVariants> & {
    isPortal?: boolean
    withArrow?: boolean
  }

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  ContentProps
>(
  (
    {
      className,
      sideOffset = 10,
      size,
      theme,
      children,
      isPortal = true,
      withArrow = true,
      ...props
    },
    ref,
  ) => {
    const content = (
      <>
        <TooltipPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          className={cn(
            tooltipContentVariants({ size, theme, className }),
            'group/tooltip',
          )}
          {...props}
        >
          {children}

          {withArrow && (
            <div
              className={cn(
                'absolute left-1/2 -translate-x-1/2',
                'group-data-[side=bottom]/tooltip:-top-xxxs',
                'group-data-[side=top]/tooltip:-bottom-xxxs',
                'group-data-[side=left]/tooltip:right-0.5 group-data-[side=left]/tooltip:top-1/2 group-data-[side=left]/tooltip:-translate-y-1/2 group-data-[side=left]/tooltip:translate-x-full',
                'group-data-[side=right]/tooltip:left-0.5 group-data-[side=right]/tooltip:top-1/2 group-data-[side=right]/tooltip:-translate-x-full group-data-[side=right]/tooltip:-translate-y-1/2',
                { 'left-xs': props.align === 'start' },
              )}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="4"
                viewBox="0 0 8 4"
                fill="none"
                className={cn(
                  'group-data-[side=bottom]/tooltip:rotate-180',
                  'group-data-[side=right]/tooltip:rotate-90',
                  'group-data-[side=left]/tooltip:rotate-270',
                )}
              >
                <path
                  d="M6.79297 0.5L4.35352 2.93945C4.15827 3.13461 3.84173 3.13461 3.64648 2.93945L1.20703 0.5H6.79297Z"
                  fill="var(--gray-800)"
                  stroke="var(--gray-800)"
                />
              </svg>
            </div>
          )}
        </TooltipPrimitive.Content>
      </>
    )

    if (isPortal) {
      return <TooltipPrimitive.Portal>{content}</TooltipPrimitive.Portal>
    }

    return content
  },
)
TooltipContent.displayName = TooltipPrimitive.Content.displayName

type TooltipWrapperProps = VariantProps<typeof tooltipContentVariants> &
  React.PropsWithChildren<{
    tooltipText: React.ReactNode
    triggerClassname?: string
    triggerProps?: TooltipPrimitive.TooltipTriggerProps
  }> &
  TooltipPrimitive.PopperContentProps

const TooltipWrapper: React.FC<TooltipWrapperProps> = ({
  children,
  tooltipText,
  triggerClassname,
  triggerProps,
  ...contentProps
}) => (
  <Tooltip>
    <TooltipTrigger className={triggerClassname} {...triggerProps}>
      {children}
    </TooltipTrigger>
    {tooltipText && (
      <TooltipContent {...contentProps}>{tooltipText}</TooltipContent>
    )}
  </Tooltip>
)

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipWrapper,
}
